import React, { useMemo, useState } from "react";
import { Chip } from "@mui/material";
import DataGrid from "../../../widgets/DataGrid/DataGrid";
import CustomDatePicker from "../../../widgets/CustomDatePicker/CustomDatePicker";
import CustomSelect from "../../../widgets/CustomSelect/CustomSelect";
import CustomTextField from "../../../widgets/CustomTextField/CustomTextField";
import AmountRangeFilter from "../../../widgets/AmountRangeFilter/AmountRangeFilter";
import {
  matchesAmountRange,
  matchesExactValue,
  matchesText,
} from "../../../DataTableUtils/DataTableUtils";
import {
  PAYMENT_FREQUENCY,
  PAYMENT_TYPE,
  PROMISE_TYPE,
  STATUS_TYPE,
} from "../../../enums/PromiseEnums";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import dayjs from "dayjs";

const matchesDate = (value, filter) => {
  if (!filter) return true;
  if (!value || value === "-") return false;
  return dayjs(value).format("YYYY-MM-DD") === dayjs(filter).format("YYYY-MM-DD");
};

const PromisesForCampaignTable = ({ rows, loading, onOpenDetails }) => {
  const { t } = useCustomTranslation("Promises List");
  const [tableFilter, setTableFilter] = useState({
    email: "",
    name: "",
    phone: "",
    created_at: null,
    payment_type: "",
    promise_type: "",
    given_amount_min: "",
    given_amount_max: "",
    promise_amount_min: "",
    promise_amount_max: "",
    payment_frequency: "",
    end_date: null,
    status: "",
  });

  const changeTableFilter = (option, value) => {
    setTableFilter((prev) => ({ ...prev, [option]: value }));
  };

  const clearTableFilter = (option) => {
    setTableFilter((prev) => ({
      ...prev,
      [option]: option === "created_at" || option === "end_date" ? null : "",
    }));
  };

  const columns = useMemo(() => [
    { field: "email", headerName: t("email"), sortable: true },
    { field: "name", headerName: t("name"), sortable: true },
    { field: "phone", headerName: t("phone"), sortable: true, numberSort: true },
    { field: "created_at", headerName: t("created_at"), sortable: true, dateSort: true, sortField: "created_at_value" },
    { field: "payment_type", headerName: t("payment_type"), sortable: true },
    { field: "promise_type", headerName: t("promise_type"), sortable: true },
    { field: "given_amount", headerName: t("given_amount"), sortable: true, numberSort: true, sortField: "given_amount_value" },
    { field: "promise_amount", headerName: t("promise_amount"), sortable: true, numberSort: true, sortField: "promise_amount_value" },
    { field: "payment_frequency", headerName: t("frequency"), sortable: true },
    { field: "end_date", headerName: t("end_date"), sortable: true, dateSort: true, sortField: "end_date_value" },
    {
      field: "status",
      headerName: t("status"),
      sortable: true,
      renderCell: (params) => {
        const map = {
          [STATUS_TYPE.IN_PROGRESS]: { label: t("in_progress"), color: "primary" },
          [STATUS_TYPE.ABANDONED]: { label: t("abandoned"), color: "error", variant: "outlined" },
          [STATUS_TYPE.CLOSED]: { label: t("closed"), color: "error" },
          [STATUS_TYPE.OPEN]: { label: t("open"), color: "success" },
          [STATUS_TYPE.CANCELLED]: { label: t("cancelled", "Cancelled"), color: "warning", variant: "outlined" },
        };
        const cfg = map[params.value] || { label: t("unknown_status"), color: "default" };
        return <Chip label={cfg.label} color={cfg.color} variant={cfg.variant || "contained"} />;
      },
    },
  ], [t]);

  const filters = {
    email: (
      <CustomTextField
        value={tableFilter.email || ""}
        onChange={(e) => changeTableFilter("email", e.target.value)}
        clearFilter={() => clearTableFilter("email")}
      />
    ),
    name: (
      <CustomTextField
        value={tableFilter.name || ""}
        onChange={(e) => changeTableFilter("name", e.target.value)}
        clearFilter={() => clearTableFilter("name")}
      />
    ),
    phone: (
      <CustomTextField
        value={tableFilter.phone || ""}
        onChange={(e) => changeTableFilter("phone", e.target.value)}
        clearFilter={() => clearTableFilter("phone")}
      />
    ),
    created_at: (
      <CustomDatePicker
        value={tableFilter.created_at}
        onChange={(newValue) => changeTableFilter("created_at", newValue)}
        clearFilter={() => clearTableFilter("created_at")}
      />
    ),
    payment_type: (
      <CustomSelect
        value={tableFilter.payment_type}
        onChange={(e) => changeTableFilter("payment_type", e.target.value)}
        options={[
          { value: "", label: t("all_text") },
          { value: PAYMENT_TYPE.CREDIT_CARD, label: t("credit_card") },
          { value: PAYMENT_TYPE.CASH, label: t("cash") },
          { value: PAYMENT_TYPE.CHEQUE, label: t("cheque") },
          { value: PAYMENT_TYPE.BANK_TRANSFER, label: t("bank_transfer") },
        ]}
      />
    ),
    promise_type: (
      <CustomSelect
        value={tableFilter.promise_type}
        onChange={(e) => changeTableFilter("promise_type", e.target.value)}
        options={[
          { value: "", label: t("all_text") },
          { value: PROMISE_TYPE.ONE_TIME_PAYMENT, label: t("one_time_payment") },
          { value: PROMISE_TYPE.SPLIT_PAYMENT, label: t("split_payment") },
        ]}
      />
    ),
    given_amount: (
      <AmountRangeFilter
        min={tableFilter.given_amount_min}
        max={tableFilter.given_amount_max}
        onMinChange={(e) => changeTableFilter("given_amount_min", e.target.value)}
        onMaxChange={(e) => changeTableFilter("given_amount_max", e.target.value)}
        onClearMin={() => clearTableFilter("given_amount_min")}
        onClearMax={() => clearTableFilter("given_amount_max")}
        t={t}
      />
    ),
    promise_amount: (
      <AmountRangeFilter
        min={tableFilter.promise_amount_min}
        max={tableFilter.promise_amount_max}
        onMinChange={(e) => changeTableFilter("promise_amount_min", e.target.value)}
        onMaxChange={(e) => changeTableFilter("promise_amount_max", e.target.value)}
        onClearMin={() => clearTableFilter("promise_amount_min")}
        onClearMax={() => clearTableFilter("promise_amount_max")}
        t={t}
      />
    ),
    payment_frequency: (
      <CustomSelect
        value={tableFilter.payment_frequency}
        onChange={(e) => changeTableFilter("payment_frequency", e.target.value)}
        options={[
          { value: "", label: t("all_text") },
          { value: "-", label: "-" },
          { value: PAYMENT_FREQUENCY.DAILY, label: t("daily") },
          { value: PAYMENT_FREQUENCY.WEEKLY, label: t("weekly") },
          { value: PAYMENT_FREQUENCY.EVERY_2_WEEKS, label: t("every_2_weeks") },
          { value: PAYMENT_FREQUENCY.MONTHLY, label: t("monthly") },
          { value: PAYMENT_FREQUENCY.EVERY_2_MONTHS, label: t("every_2_months") },
          { value: PAYMENT_FREQUENCY.QUARTERLY, label: t("quarterly") },
          { value: PAYMENT_FREQUENCY.EVERY_4_MONTHS, label: t("every_4_months") },
          { value: PAYMENT_FREQUENCY.BIMONTHLY, label: t("bimonthly") },
          { value: PAYMENT_FREQUENCY.SEMI_ANNUALLY, label: t("semi_annually") },
          { value: PAYMENT_FREQUENCY.ANNUALLY, label: t("annually") },
        ]}
      />
    ),
    end_date: (
      <CustomDatePicker
        value={tableFilter.end_date}
        onChange={(newValue) => changeTableFilter("end_date", newValue)}
        clearFilter={() => clearTableFilter("end_date")}
      />
    ),
    status: (
      <CustomSelect
        value={tableFilter.status}
        onChange={(e) => changeTableFilter("status", e.target.value)}
        options={[
          { value: "", label: t("all_text") },
          { value: STATUS_TYPE.IN_PROGRESS, label: t("in_progress") },
          { value: STATUS_TYPE.ABANDONED, label: t("abandoned") },
          { value: STATUS_TYPE.CLOSED, label: t("closed") },
          { value: STATUS_TYPE.OPEN, label: t("open") },
          { value: STATUS_TYPE.CANCELLED, label: t("cancelled", "Cancelled") },
        ]}
      />
    ),
  };

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesText(row.email, tableFilter.email) &&
          matchesText(row.name, tableFilter.name) &&
          matchesText(row.phone, tableFilter.phone) &&
          matchesDate(row.created_at, tableFilter.created_at) &&
          matchesExactValue(row.payment_type_value, tableFilter.payment_type) &&
          matchesExactValue(row.promise_type_value, tableFilter.promise_type) &&
          matchesAmountRange(
            row.given_amount_value,
            tableFilter.given_amount_min,
            tableFilter.given_amount_max
          ) &&
          matchesAmountRange(
            row.promise_amount_value,
            tableFilter.promise_amount_min,
            tableFilter.promise_amount_max
          ) &&
          matchesExactValue(row.payment_frequency_value, tableFilter.payment_frequency) &&
          matchesDate(row.end_date, tableFilter.end_date) &&
          matchesExactValue(row.status, tableFilter.status)
      ),
    [rows, tableFilter]
  );

  return (
    <div className="container-white">
      <DataGrid
        columns={columns}
        rows={filteredRows}
        filters={filters}
        loading={loading}
        showDetails={onOpenDetails}
      />
    </div>
  );
};

export default PromisesForCampaignTable;
